import dateFormat from 'dateformat';
import { Flex, Text } from '@chakra-ui/react';
import { faFile, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { Banner, OutlineCard, Table } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BaseBadge, recommendationBadges } from '../Review/Badges';
import { ReviewFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/review-fields/review-form-fields';
import {
  consentAccordionContent,
  optInAccordionContent,
  submissionScoreTableContent
} from './ReviewPreviewModal';
import { RATING_OPTIONS } from 'src/utils/reviews';
import { areaOfExpertiseOptionMap } from '@publishing-engine/domain/utils';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import { FormFieldRadio } from '@publishing-engine/domain/types/article';
import BetterSuitedForAnotherJournal from '../Submission/ReviewTab/BetterSuitedForAnotherJournal';
import { MissingReference } from '../Submission/ReviewTab/MissingRefrence';
import FileViewerButtonText from '../Submission/FileViewerButtonText';
import { DisplayField } from 'src/components/shared/DisplayField';

export const ReviewedPage: React.FC<{}> = () => {
  const { review, reviewerFormSchema, organizationData } = useReviewData();
  const {
    submissionContextId,
    workflow,
    caseData: { firstAssessment }
  } = review;
  const reviewedDate = workflow.states?.find(state => state.name === 'inReview')
    ?.completedWith?.date;

  const reviewFormFields = new ReviewFormFields(
    reviewerFormSchema.documentSchema
  );
  const configs: any = {
    Recommendation: reviewFormFields.getFieldConfig(
      'Recommendation'
    ) as unknown as FormFieldRadio,
    // submission scores
    Originality: reviewFormFields.getFieldConfig('Originality'),
    Significance: reviewFormFields.getFieldConfig('Significance'),
    Rigour: reviewFormFields.getFieldConfig('Rigour'),
    Clarity: reviewFormFields.getFieldConfig('Clarity'),
    Impact: reviewFormFields.getFieldConfig('Impact'),
    FeedbackForAuthors: reviewFormFields.getFieldConfig('FeedbackForAuthors'),
    ReviewerAttachments: reviewFormFields.getFieldConfig('ReviewerAttachments'),
    ReviewerAttachmentsEditor: reviewFormFields.getFieldConfig(
      'ReviewerAttachmentsEditor'
    ),
    MissingReference: reviewFormFields.getFieldConfig('MissingReference'),
    ConflictOfInterest: reviewFormFields.getFieldConfig('ConflictOfInterest'),
    CanArticleBeShortened: reviewFormFields.getFieldConfig(
      'CanArticleBeShortened'
    ),
    MeritFurtherCoverageUponPublication: reviewFormFields.getFieldConfig(
      'MeritFurtherCoverageUponPublication'
    ),
    AreaOfExpertise: reviewFormFields.getFieldConfig('AreaOfExpertise'),
    BetterSuitedForAnotherJournal: reviewFormFields.getFieldConfig(
      'BetterSuitedForAnotherJournal'
    ),
    FeedbackForEditors: reviewFormFields.getFieldConfig('FeedbackForEditors'),
    ArticleWellWritten: reviewFormFields.getFieldConfig('ArticleWellWritten'),
    ReviewConfirmation: reviewFormFields.getFieldConfig('ReviewConfirmation'),
    UnaddressedReviewComments: reviewFormFields.getFieldConfig(
      'UnaddressedReviewComments'
    ),
    // Opt Ins
    ReceiveFeedback: reviewFormFields.getFieldConfig('ReceiveFeedback'),
    WebOfScienceOptIn: reviewFormFields.getFieldConfig('WebOfScienceOptIn'),
    PeerReviewTransparency: reviewFormFields.getFieldConfig(
      'PeerReviewTransparency'
    ),
    ReviewerAnonymity: reviewFormFields.getFieldConfig('ReviewerAnonymity'),
    ConsentToReInvite: reviewFormFields.getFieldConfig('ConsentToReInvite'),
    ReviewersEthicalPolicy: reviewFormFields.getFieldConfig(
      'ReviewersEthicalPolicy'
    )
  };

  const getAreaOfExpertise = (areaOfExpertise: string) => {
    return areaOfExpertiseOptionMap[
      areaOfExpertise as keyof typeof areaOfExpertiseOptionMap
    ];
  };

  return (
    <Flex flexDirection="column" gap={4} p={2}>
      <Flex flexDirection="column">
        <Banner
          content={
            <Text textStyle={'textContext'}>
              Your review was sent to the editors on{' '}
              {dateFormat(reviewedDate, 'mmmm d, yyyy')}. You can only review
              the sent information.
            </Text>
          }
          heading="Your Review"
          variant="success"
        />
      </Flex>
      {firstAssessment?.recommendation && (
        <Flex flexDirection="column" gap={1}>
          <Text textStyle="headingGroup">Recommendation</Text>
          <Flex>
            {recommendationBadges[firstAssessment.recommendation] ||
              configs.Recommendation?.params?.labels?.options[
                firstAssessment.recommendation
              ]?.main}
          </Flex>
        </Flex>
      )}
      {firstAssessment?.submissionScores && (
        <OutlineCard p={4}>
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingGroup">Submission Scores</Text>

            <Flex flexDirection="column">
              <Table>
                <Table.HeaderRow>
                  <Table.HeaderCell flex="1" textAlign="left">
                    Criteria
                  </Table.HeaderCell>
                  <Table.HeaderCell flex="1" textAlign="left">
                    Score
                  </Table.HeaderCell>
                </Table.HeaderRow>
                <Table.Body sx={{ borderBottom: 'none' }}>
                  <>{submissionScoreTableContent(firstAssessment, configs)}</>
                </Table.Body>
              </Table>
            </Flex>
          </Flex>
        </OutlineCard>
      )}

      {(firstAssessment?.feedbackForAuthors?.original ||
        firstAssessment?.reviewerAttachments?.length ||
        firstAssessment?.missingReference?.exists) && (
        <Flex flexDirection="column" gap={4}>
          <OutlineCard padding={4}>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">For Authors</Text>
              {firstAssessment?.feedbackForAuthors?.original && (
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingItem">Feedback for Authors</Text>
                  <Text textStyle="textBody">
                    {firstAssessment.feedbackForAuthors.original}
                  </Text>
                </Flex>
              )}
              {firstAssessment?.reviewerAttachments?.length ? (
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingItem">Review Attachments</Text>

                  <Flex flexDirection="column" gap={2}>
                    {firstAssessment.reviewerAttachments.map(
                      (attachment, index) => (
                        <Flex justifyContent="space-between" key={index}>
                          <Flex gap={2} alignItems="center">
                            <FontAwesomeIcon icon={faFile} />
                            <Text
                              textStyle="textBody"
                              wordBreak={'break-word'}
                              whiteSpace={'normal'}
                            >
                              {attachment.name}
                            </Text>
                            <FileViewerButtonText
                              file={attachment}
                              submissionContextId={submissionContextId}
                            >
                              View
                            </FileViewerButtonText>
                          </Flex>
                        </Flex>
                      )
                    )}
                  </Flex>
                </Flex>
              ) : null}
            </Flex>
          </OutlineCard>

          <OutlineCard p={4}>
            <Flex flexDirection="column" gap={2}>
              {(firstAssessment?.areaOfExpertise ||
                firstAssessment?.betterSuitedForAnotherJournal?.exists ||
                firstAssessment?.feedbackForEditors) && (
                <Flex flexDirection="column" gap={4}>
                  <Text textStyle="headingGroup">For Editors</Text>
                  {firstAssessment?.missingReference?.value ? (
                    <Flex flexDirection="column" gap={2}>
                      <Text textStyle="headingItem">
                        References to Recent/Appropriate Work
                      </Text>
                      <Text textStyle="textBody">
                        The author has not referenced the most recent and
                        appropriate work.
                      </Text>
                      <Text textStyle="textBody">
                        {firstAssessment.missingReference.value}
                      </Text>
                    </Flex>
                  ) : (
                    <MissingReference
                      config={configs?.MissingReference}
                      missingReference={
                        firstAssessment?.missingReference || null
                      }
                    />
                  )}
                </Flex>
              )}

              {firstAssessment?.articleWellWritten && (
                <DisplayField
                  fieldName="ArticleWellWritten"
                  value={firstAssessment.articleWellWritten}
                  config={configs}
                />
              )}
              {firstAssessment?.areaOfExpertise && (
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingItem">Area of Expertise</Text>
                  <Text textStyle="textBody">
                    This Submission is{' '}
                    {getAreaOfExpertise(firstAssessment.areaOfExpertise)} in the
                    Reviewer’s area of expertise.
                  </Text>
                </Flex>
              )}

              {configs?.BetterSuitedForAnotherJournal && (
                <BetterSuitedForAnotherJournal
                  config={configs?.BetterSuitedForAnotherJournal}
                  betterSuitedForAnotherJournal={
                    firstAssessment?.betterSuitedForAnotherJournal || null
                  }
                  organizationData={organizationData}
                />
              )}

              {firstAssessment?.feedbackForEditors && (
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingItem">Feedback for Editors</Text>
                  <Text textStyle="textBody">
                    {firstAssessment.feedbackForEditors}
                  </Text>
                </Flex>
              )}
              {firstAssessment?.reviewerAttachmentsEditor?.length ? (
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingItem">
                    Review Attachments for Editor
                  </Text>

                  <Flex flexDirection="column" gap={2}>
                    {firstAssessment.reviewerAttachmentsEditor.map(
                      (attachment, index) => (
                        <Flex justifyContent="space-between" key={index}>
                          <Flex gap={2} alignItems="center">
                            <FontAwesomeIcon icon={faFile} />
                            <Text
                              textStyle="textBody"
                              whiteSpace={'normal'}
                              wordBreak={'break-word'}
                            >
                              {attachment.name}
                            </Text>
                            <FileViewerButtonText
                              file={attachment}
                              submissionContextId={submissionContextId}
                            >
                              View
                            </FileViewerButtonText>
                          </Flex>
                        </Flex>
                      )
                    )}
                  </Flex>
                </Flex>
              ) : null}

              {firstAssessment?.conflictOfInterest && (
                <DisplayField
                  fieldName="ConflictOfInterest"
                  value={firstAssessment.conflictOfInterest}
                  config={configs}
                />
              )}
              {firstAssessment?.canArticleBeShortened && (
                <DisplayField
                  fieldName="CanArticleBeShortened"
                  value={firstAssessment.canArticleBeShortened}
                  config={configs}
                />
              )}
              {firstAssessment?.meritFurtherCoverageUponPublication && (
                <DisplayField
                  fieldName="MeritFurtherCoverageUponPublication"
                  value={firstAssessment.meritFurtherCoverageUponPublication}
                  config={configs}
                />
              )}
            </Flex>
          </OutlineCard>
        </Flex>
      )}
      {firstAssessment?.receiveFeedback === 'yes' && (
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingItem">Review Score</Text>
          {firstAssessment?.reviewScore?.score ? (
            <>
              <Text textStyle="textBody">
                Review scored as{' '}
                {RATING_OPTIONS[firstAssessment?.reviewScore?.score]?.value} (
                {RATING_OPTIONS[firstAssessment?.reviewScore?.score]?.label})
              </Text>
              {firstAssessment?.reviewScore?.justification?.trim() && (
                <>
                  <Text textStyle="headingItem" gap={4}>
                    Review Feedback
                  </Text>
                  <Text textStyle="textBody">
                    {firstAssessment.reviewScore.justification}
                  </Text>
                </>
              )}
            </>
          ) : (
            <Flex>
              <BaseBadge
                icon={faRefresh}
                variant="attention"
                label="Waiting for Feedback"
              />
            </Flex>
          )}
        </Flex>
      )}
      <OutlineCard p={4}>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">Opt Ins</Text>
          {firstAssessment &&
            optInAccordionContent(firstAssessment, configs, 0)}
        </Flex>
      </OutlineCard>
      {firstAssessment?.consentToReInvite && (
        <OutlineCard p={4}>
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingGroup">Consent</Text>
            {firstAssessment &&
              consentAccordionContent(firstAssessment, configs, 0)}
          </Flex>
        </OutlineCard>
      )}
    </Flex>
  );
};
